import { usePatchAdminUserActiveStatus } from '@sit/client-shared';
import { noop } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import Card, { CardColumn, CardRow } from 'carbon-react/lib/components/card';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Divider from 'carbon-react/lib/components/divider';
import { GridContainer, GridItem } from 'carbon-react/lib/components/grid';
import Typography from 'carbon-react/lib/components/typography';
import { useParams } from 'react-router';
import { useGetApiUser } from '../../api/use-get-api-user';
import GoBackButton from '../../components/GoBackButton';

function MainContainer({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="intacct-admin">
      <Box display="flex">
        <GoBackButton url="/companies" width="auto" />
        <Typography variant="h1">{`Users - ${title}`}</Typography>
      </Box>
      <div className="intacct-admin-inner-container">{children}</div>
    </div>
  );
}

export function UserPage() {
  const { id: userId } = useParams() as { id: string };
  const { data: userResponse, isLoading, isError } = useGetApiUser(userId);
  const { mutate: patchActiveStatus } = usePatchAdminUserActiveStatus();

  let title: string;
  if (isError) {
    title = 'User not found';
  } else if (isLoading || !userResponse) {
    title = 'Loading...';
  } else {
    title = userResponse.user.username;
  }

  const updateActiveStatus = (active: boolean) => {
    patchActiveStatus({ pathParams: { id: userId }, body: { active } });
  };

  if (!userResponse) {
    return <MainContainer title={title}>Loading...</MainContainer>;
  }
  const { user } = userResponse;

  const {
    canListMyTimesheets,
    canViewMyTimesheets,
    email,
    firstName,
    hasStaffTimesheets,
    isActive,
    isVerified,
    lastName,
    timezoneOffset,
    username,
  } = user;

  return (
    <MainContainer title={title}>
      <GridContainer>
        <GridItem justifySelf="center" gridColumn="1 / 2">
          <Card>
            <CardRow pb={0}>
              <CardColumn align="left">
                <Typography variant="h1" mt={2}>
                  User details
                </Typography>
              </CardColumn>
            </CardRow>
            <Divider type="horizontal" mt={2} mb={2} />
            <CardRow p={0}>
              <CardColumn align="left">
                <Box>
                  <Typography>
                    <strong>Username:</strong> {username}
                  </Typography>
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow p={0}>
              <CardColumn align="left">
                <Box>
                  <Typography>
                    <strong>First name:</strong> {firstName}
                  </Typography>
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow p={0}>
              <CardColumn align="left">
                <Box>
                  <Typography>
                    <strong>Last name:</strong> {lastName}
                  </Typography>
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow p={0}>
              <CardColumn align="left">
                <Box>
                  <Typography>
                    <strong>Email:</strong> {email}
                  </Typography>
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow p={0}>
              <CardColumn align="left">
                <Box>
                  <Typography>
                    <strong>Timezone offset:</strong> {timezoneOffset ?? 'N/A'}
                  </Typography>
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow py={1}>
              <CardColumn align="left">
                <Box>
                  <Checkbox
                    label="Can list my timesheets"
                    checked={canListMyTimesheets}
                    onChange={noop}
                  />
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow py={1}>
              <CardColumn align="left">
                <Box>
                  <Checkbox
                    label="Can view my timesheets"
                    checked={canViewMyTimesheets}
                    onChange={noop}
                  />
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow py={1}>
              <CardColumn align="left">
                <Box>
                  <Checkbox
                    label="Has staff timesheets"
                    checked={hasStaffTimesheets}
                    onChange={noop}
                  />
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow py={1}>
              <CardColumn align="left">
                <Box>
                  <Checkbox
                    label="Is verified"
                    checked={isVerified}
                    onChange={noop}
                  />
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow pt={1}>
              <CardColumn align="left">
                <Box>
                  <Checkbox
                    label="Is active"
                    checked={isActive}
                    onChange={noop}
                  />
                </Box>
              </CardColumn>
            </CardRow>
          </Card>
        </GridItem>
        <GridItem justifySelf="center" gridColumn="1 / 2">
          <Card>
            <CardRow pb={0}>
              <CardColumn align="left">
                <Typography variant="h1" mt={2}>
                  User active state
                </Typography>
              </CardColumn>
            </CardRow>
            <Divider type="horizontal" mt={2} mb={2} />
            <CardRow p={0}>
              <CardColumn align="left">
                <Box>
                  <Typography fontSize="16px" mb={0}>
                    An active user can log in to SIT, while an inactive user
                    cannot. This applies to both standalone and embedded users.
                  </Typography>
                </Box>
              </CardColumn>
            </CardRow>
            <CardRow>
              <CardColumn align="left">
                <Box>
                  <Checkbox
                    label="Is active"
                    checked={isActive}
                    onChange={noop}
                  />
                </Box>
              </CardColumn>
              <CardColumn align="left">
                <Box>
                  <Button onClick={() => updateActiveStatus(true)}>
                    Activate
                  </Button>
                </Box>
              </CardColumn>
              <CardColumn align="left">
                <Box>
                  <Button
                    onClick={() => updateActiveStatus(false)}
                    variant="destructive"
                  >
                    Deactivate
                  </Button>
                </Box>
              </CardColumn>
            </CardRow>
          </Card>
        </GridItem>
      </GridContainer>
    </MainContainer>
  );
}
